import React, { useState, useRef, useEffect } from 'react';
import {
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Button,
  Input,
  VStack,
  HStack,
  Text,
  Box,
  Avatar,
  IconButton,
  useToast,
  Flex,
  Textarea,
  Badge,
  Spinner,
  Icon,
  Image
} from '@chakra-ui/react';
import { FiSend, FiImage } from 'react-icons/fi';
import { FaRobot } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { Project } from '../../context/ProjectContext';
import { motion } from "framer-motion";

// Import Profile Images
import abuProfile from '../../assets/img/abuProfile.jpg';
import mengaProfile from '../../assets/img/mengaProfile.jpg';

const MotionBox = motion(Box);

interface ChatWithAbuProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string | undefined;
  currentUser: any;
  setProject: React.Dispatch<React.SetStateAction<Project | null>>;
  pendingMessage?: { content: string, agentId?: string } | null;
  onMessageConsumed?: () => void;
}

interface Message {
  id: string;
  role: 'user' | 'agent';
  content: string;
  type?: 'text' | 'image' | 'competitor';
  data?: any;
  timestamp: number;
  agentId?: string; // 'abu' or 'menga'
}

const ChatWithAbu: React.FC<ChatWithAbuProps> = ({
  isOpen,
  onClose,
  projectId,
  currentUser,
  setProject,
  pendingMessage,
  onMessageConsumed
}) => {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<Message[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  
  const toast = useToast();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isOpen && projectId && currentUser) {
        loadHistory();
    }
  }, [isOpen, projectId, currentUser]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isOpen, isLoading]);

  // Handle Pending Message (Auto-send)
  useEffect(() => {
      if (isOpen && pendingMessage && !isLoading) {
          const { content, agentId } = pendingMessage;
          handleSendMessage(content, agentId);
          if (onMessageConsumed) onMessageConsumed();
      }
  }, [isOpen, pendingMessage, isLoading]);

  const getToken = async () => {
      return await currentUser?.getIdToken();
  };

  const loadHistory = async () => {
      if (!projectId) return;
      setIsInitializing(true);
      try {
          const token = await getToken();
          const response = await fetch(`/.netlify/functions/chatWithAbu?projectId=${projectId}`, {
              headers: { Authorization: `Bearer ${token}` }
          });
          
          if (response.ok) {
              const data = await response.json();
              
              // Robust parsing: Handle array directly or object wrapper
              const historyMessages = Array.isArray(data) ? data : (data.messages || []);
              const suggestions = !Array.isArray(data) ? (data.suggestedReplies || []) : [];

              if (historyMessages.length > 0) setMessages(historyMessages);
              if (suggestions.length > 0) setSuggestions(suggestions);
              
              // If empty, add welcome message locally
              if (historyMessages.length === 0) {
                  setMessages([{
                      id: 'welcome',
                      role: 'agent',
                      content: "Hello ! Je suis Abu. Comment puis-je vous aider à optimiser ce produit ?",
                      timestamp: Date.now(),
                      agentId: 'abu'
                  }]);
                  // Default suggestions if none
                  if (suggestions.length === 0) {
                      setSuggestions(["Optimiser le titre", "Analyser les concurrents", "Revoir les bullet points"]);
                  }
              }
          }
      } catch (error) {
          console.error("Failed to load history", error);
      } finally {
          setIsInitializing(false);
      }
  };

  const handleSendMessage = async (text?: string, agentId?: string) => {
    const contentToSend = text || inputValue;
    if (!contentToSend.trim()) return;

    const tempId = Date.now().toString();
    const newMessage: Message = {
      id: tempId,
      role: 'user',
      content: contentToSend,
      timestamp: Date.now()
    };

    setMessages(prev => [...prev, newMessage]);
    setInputValue('');
    setSuggestions([]); // Clear suggestions while waiting
    setIsLoading(true);

    try {
      const token = await getToken();
      const response = await fetch('/.netlify/functions/chatWithAbu', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
            projectId,
            message: contentToSend,
            type: 'text',
            agent: agentId // Pass agent selection to backend
        })
      });

      const data = await response.json();

      if (!response.ok) {
          throw new Error(data.error || "Failed to send");
      }

      // Add Agent Reply
      if (data.reply) {
          const agentMessage: Message = {
              id: (Date.now() + 1).toString(),
              role: 'agent',
              content: data.reply,
              timestamp: Date.now(),
              agentId: data.agentId || 'abu' // Backend should return this, default to Abu
          };
          setMessages(prev => [...prev, agentMessage]);
      }

      // Update Suggestions
      if (data.suggestedReplies) {
          setSuggestions(data.suggestedReplies);
      }

      // Handle Events (Side Effects)
      if (data.events) {
          data.events.forEach((event: any) => {
              if (event.type === 'PROJECT_UPDATED') {
                  setProject(prev => prev ? ({ ...prev, ...event.data }) : null);
                  toast({ title: "Projet mis à jour", status: "success", duration: 2000 });
              }
              if (event.type === 'COMPETITOR_ADDED') {
                  toast({ title: "Concurrent ajouté", description: event.data.title, status: "info" });
              }
          });
      }

    } catch (error) {
      console.error("Chat error:", error);
      toast({
        title: "Erreur",
        description: "Impossible d'envoyer le message",
        status: "error",
      });
      // Optionally remove user message or show error state
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
      <DrawerOverlay />
      <DrawerContent bg="gray.900" color="white">
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px" borderColor="gray.700">
          <Flex align="center" gap={3}>
            <Avatar name="Abu" src={abuProfile} bg="teal.500" size="sm" />
            <Text>Chat with Abu</Text>
            <Badge colorScheme="teal" variant="solid" fontSize="0.6em">BETA</Badge>
          </Flex>
        </DrawerHeader>

        <DrawerBody>
          {isInitializing ? (
              <Flex justify="center" align="center" h="100%">
                  <Spinner color="teal.500" />
              </Flex>
          ) : (
          <VStack spacing={4} align="stretch" py={4}>
            {messages.map((msg) => (
              <Box
                key={msg.id}
                alignSelf={msg.role === 'user' ? 'flex-end' : 'flex-start'}
                maxW="85%"
              >
                {/* Agent Header */}
                {msg.role === 'agent' && (
                    <Flex align="center" gap={2} mb={1}>
                        <Avatar 
                            src={msg.agentId === 'menga' ? mengaProfile : abuProfile} 
                            size="xs" 
                            name={msg.agentId === 'menga' ? 'Menga' : 'Abu'}
                        />
                        <Text fontSize="xs" fontWeight="bold" color="gray.400">
                            {msg.agentId === 'menga' ? 'Menga' : 'Abu'}
                        </Text>
                    </Flex>
                )}

                <Box
                  bg={msg.role === 'user' ? 'teal.600' : 'gray.700'}
                  p={3}
                  borderRadius="lg"
                  borderBottomRightRadius={msg.role === 'user' ? 0 : 'lg'}
                  borderTopLeftRadius={msg.role === 'agent' ? 0 : 'lg'}
                >
                  <Text fontSize="sm" whiteSpace="pre-wrap">{msg.content}</Text>
                </Box>
                <Text fontSize="xs" color="gray.500" mt={1} textAlign={msg.role === 'user' ? 'right' : 'left'}>
                  {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </Text>
              </Box>
            ))}
            {isLoading && (
               <Box alignSelf="flex-start" bg="gray.700" p={3} borderRadius="lg" borderTopLeftRadius={0}>
                 <HStack spacing={1} as={motion.div}
                    initial="initial"
                    animate="animate"
                    variants={{
                        animate: { transition: { staggerChildren: 0.2 } }
                    }}
                 >
                    {[0, 1, 2].map((i) => (
                        <MotionBox
                            key={i}
                            w="6px"
                            h="6px"
                            bg="teal.300"
                            borderRadius="full"
                            variants={{
                                initial: { y: 0 },
                                animate: {
                                    y: [0, -8, 0],
                                    transition: {
                                        duration: 0.6,
                                        repeat: Infinity,
                                        ease: "easeInOut"
                                    }
                                }
                            }}
                        />
                    ))}
                 </HStack>
               </Box>
            )}
            <div ref={messagesEndRef} />
          </VStack>
          )}
        </DrawerBody>

        <DrawerFooter borderTopWidth="1px" borderColor="gray.700" p={2} flexDirection="column">
          {/* Suggested Replies */}
          {suggestions.length > 0 && !isLoading && (
             <HStack w="100%" spacing={2} overflowX="auto" pb={3} css={{ '&::-webkit-scrollbar': { display: 'none' } }}>
                {suggestions.map((suggestion, idx) => (
                    <Button 
                        key={idx} 
                        size="xs" 
                        variant="outline" 
                        colorScheme="teal" 
                        borderRadius="full" 
                        whiteSpace="nowrap"
                        onClick={() => handleSendMessage(suggestion)}
                    >
                        {suggestion}
                    </Button>
                ))}
             </HStack>
          )}
             
             <HStack w="100%">
                <IconButton
                  aria-label="Upload image"
                  icon={<FiImage />}
                  variant="ghost"
                  colorScheme="gray"
                  isDisabled={isLoading}
                  // onClick={triggerImageUpload}
                />
                <Textarea
                  placeholder="Posez une question..."
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyPress}
                  resize="none"
                  rows={1}
                  bg="gray.800"
                  border="none"
                  _focus={{ ring: 1, ringColor: "teal.500" }}
                  isDisabled={isLoading}
                />
                <IconButton
                  aria-label="Send"
                  icon={<FiSend />}
                  colorScheme="teal"
                  onClick={() => handleSendMessage()}
                  isLoading={isLoading}
                  isDisabled={!inputValue.trim()}
                />
             </HStack>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default ChatWithAbu;
